"use client";

import { TableCell, TableRow } from "@/components/ui/table";

import { Button } from "@/components/ui/button";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type { ProductRow } from "@/pages/admin/activation/products/columns"; // Adjust path as necessary
import type { Row } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { useSortable } from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";

interface SortableProductRowProps {
  productRow: Row<ProductRow>;
}

export function SortableProductRow({ productRow }: SortableProductRowProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: productRow.original.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0, // Ensure dragging item is on top
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      data-state={productRow.getIsSelected() && "selected"}
      className={`hover:bg-muted/50 ${isDragging ? "shadow-lg" : ""}`}
    >
      {/* Drag Handle Cell */}
      <TableCell className="w-12 text-center px-2">
        <Button
          variant="ghost"
          size="sm"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing"
          aria-label={t('dnd.dragToReorder')}
        >
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </Button>
      </TableCell>

      {/* Original Cells */}
      {productRow.getVisibleCells().map((cell) => (
        <TableCell key={cell.id} className="py-3">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );
}